<template>
    <div
        ref="plotlyChart"
        class="plotly-chart"
        style="width: 100%; height: 100%"
    ></div>
</template>

<script setup lang="ts">
import {
    nextTick,
    onMounted,
    watch,
    onUnmounted,
    useTemplateRef,
    ref,
} from "vue"
import Plotly, { PlotData } from "plotly.js-dist-min"
import { buildPlotlyBaseConfig } from "@modules/data/utils/plotlyChartConfig"
// @ts-expect-error - plotly.js-locales n'est pas typé
import locale from "plotly.js-locales/fr"
import { createWorker } from "@modules/data/workers/workerLoader"
Plotly.register(locale)
Plotly.setPlotConfig({ locale: "fr" })

/** Layout Plotly libre : barres empilées, courbes, seuils, etc. */
type Graph = {
    data: object[]
    layout: Record<string, unknown>
    /** Réservé ; `renderChart` applique une config locale (locale, mode bar…). */
    config?: object
}

type Props = {
    /**
     * Objet contenant les données du graphique.
     * - `data` Tableau d'objets qui contient les données à afficher.
     * - `layout` Objet qui configure l'apparence et la disposition du graphique.
     * - `config` Objet pour configurer les options d'interaction avec le graphique.
     */
    graph: Graph | null
    enableExport?: boolean
    /**
     * Respecter le `xaxis.range` que l'appelant a posé dans son layout.
     *
     * Par défaut, le premier tracé se termine par un `xaxis.autorange` : l'axe
     * des abscisses montre tout ce qui est chargé. C'est ce qu'attendent tous
     * les appelants historiques, et rien ne change pour eux. La page « Données
     * du compteur » (DEV-1508), elle, DÉCIDE de son axe : elle montre la
     * fenêtre regardée, pas tout ce que le chargement a ramené autour. Cette
     * option, et elle seule, laisse le range posé tranquille.
     */
    preserveLayoutRange?: boolean
    /**
     * Boutons de la barre d'outils Plotly à retirer EN PLUS de la liste
     * historique. Additif : sans cette prop, tout appelant garde exactement la
     * barre qu'il avait. La page « Données du compteur » (DEV-1508) y met
     * `autoScale2d`, qui réétendait l'axe des temps à tout ce qui est chargé,
     * au-delà de la fenêtre regardée — l'inverse de ce que la page promet.
     */
    modeBarButtonsToRemoveExtra?: string[]
    /**
     * Nom du fichier de l'image téléchargée par le bouton appareil photo.
     *
     * Sans elle, Plotly télécharge `newplot.png`. L'appelant qui la fournit
     * nomme son fichier lui-même ; à lui de n'y mettre aucun mot interne ni nom
     * de société, le nom du fichier étant une surface que le client voit.
     */
    toImageFilename?: string
}

// `relayout` porte la fenêtre de l'axe des temps après un cliquer-glisser ;
// `doubleclick` le geste de retour. Les deux sont ADDITIFS : un appelant qui ne
// les écoute pas se comporte exactement comme avant (DEV-1508 lot 2, E6).
const emit = defineEmits(["loaded", "click:export", "relayout", "doubleclick"])

const props = withDefaults(defineProps<Props>(), {
    enableExport: false,
    preserveLayoutRange: false,
    modeBarButtonsToRemoveExtra: () => [],
    toImageFilename: undefined,
})
const plotlyChart = useTemplateRef("plotlyChart")

/** `true` après le premier `Plotly.newPlot` — les mises à jour suivantes utilisent `animate` / `react`. */
const isPlotInitialized = ref(false)

/**
 * COMBIEN de redessins à NOUS sont en cours (newPlot, react/animate, autoscale,
 * resize). Zéro veut dire : ce que Plotly émet maintenant vient de la MAIN de
 * l'utilisateur.
 *
 * Plotly émet `plotly_relayout` pour ses propres recalculs d'axes, pas seulement
 * pour un geste. Sans cette garde, un appelant qui écoute `relayout` pour
 * recadrer (DEV-1508 lot 2) reçoit un événement à chaque redraw, recharge,
 * redessine, reçoit un événement... et le FIL PRINCIPAL NE REND PLUS LA MAIN.
 * C'est ce qui a figé l'onglet de la page « Données du compteur » sur staging le
 * 10/09 (deux captures d'écran expirées, « page busy »).
 *
 * Pourquoi un COMPTEUR et non un booléen (DEV-1508 lot 2d) : deux redessins se
 * chevauchent tout le temps sur cette page — la fenêtre regardée change, puis
 * les points arrivent. Avec un booléen, le premier à finir baissait la garde
 * pendant que le second peignait encore, et les recalculs d'axes du second
 * étaient pris pour un cliquer-glisser. Ce que ça donnait à l'écran, en vrai,
 * dans Chrome : « Revenir au contexte » cliqué dans la demi-seconde qui suit un
 * brush était ANNULÉ — la page repartait sur la fenêtre du brush et y restait.
 *
 * La règle : on n'émet `relayout` et `doubleclick` que pour ce qui vient de la
 * MAIN de l'utilisateur. Un appelant qui n'écoute pas ces événements ne voit
 * aucune différence.
 */
const redessinsEnCours = ref(0)

/** Vrai tant qu'un redessin à nous est en cours. */
function redessinEnCours(): boolean {
    return redessinsEnCours.value > 0
}

/** Tient la garde levée le temps d'un redessin, chevauchements compris. */
async function pendantUnRedessin(faire: () => Promise<void>): Promise<void> {
    redessinsEnCours.value += 1
    try {
        await faire()
    } finally {
        redessinsEnCours.value -= 1
    }
}

/** Référence Plotly renvoyée par `newPlot` (typage correct pour `animate` / `react`). */
const plotlyGraphDiv = ref<Plotly.PlotlyHTMLElement | null>(null)

/** Observer la taille du conteneur pour recaler Plotly (évite le débordement hors cadre flex). */
let plotResizeObserver: ResizeObserver | null = null

const worker = createWorker()

/**
 * Vrai quand l'appelant pose lui-même les bornes de l'axe des temps ET demande
 * qu'on les respecte. Dans ce cas, aucun `autorange` de notre part : il
 * effacerait la fenêtre voulue au premier tracé.
 */
function layoutPoseUnRange(): boolean {
    if (!props.preserveLayoutRange) return false
    const xaxis = props.graph?.layout?.xaxis as { range?: unknown } | undefined
    return Array.isArray(xaxis?.range) && xaxis.range.length === 2
}

/** Durée et easing des transitions entre jeux de données (scatter, mêmes traces). */
const DATA_UPDATE_ANIMATION_MS = 500
const DATA_UPDATE_EASING = "cubic-in-out"

type RangeData = { min: number; max: number | null }

// Recevoir le résultat du Web Worker
worker.onmessage = function (e: {
    data: {
        y1y2: RangeData
        y3: RangeData | null
    }
}) {
    function handleMinMaxLayout(data: RangeData, axis: string[]) {
        if (!plotlyChart.value) return

        // Si les données sont nulles, ne rien faire
        if (data.max === null) return

        const relayout = {} as Record<string, any>

        const newYRange = [data.min < 0 ? data.min : 0, data.max] as [
            number,
            number,
        ]

        for (const ax of axis) {
            if (props.graph?.layout?.[ax as keyof typeof props.graph.layout]) {
                const layout = props.graph.layout as Record<string, any>

                const axisLayout = layout[ax]
                if (!axisLayout) continue

                relayout[ax] = {
                    ...axisLayout,
                    range: newYRange,
                }
            }
        }

        Plotly.relayout(plotlyChart.value, relayout)
    }

    handleMinMaxLayout(e.data.y1y2, ["yaxis", "yaxis2"])

    if (e.data.y3) {
        handleMinMaxLayout(e.data.y3, ["yaxis3"])
    }
}

// Gérer les erreurs du Web Worker (facultatif)
worker.onerror = function (error) {
    console.error("Erreur dans le Web Worker", error)
    worker.terminate()
}

/**
 * Config Plotly locale (mode bar, export, locale) — stable pour `react` / même instance.
 */
function buildPlotConfig(): Record<string, unknown> {
    const config = buildPlotlyBaseConfig({
        modeBarButtonsToRemoveExtra: props.modeBarButtonsToRemoveExtra,
        toImageFilename: props.toImageFilename,
    })
    if (props.enableExport) {
        ;(config as Record<string, unknown>).modeBarButtonsToAdd = [
            {
                name: "exportdata",
                title: "Exporter les données",
                icon: Plotly.Icons.disk,
                click: () => {
                    emit("click:export")
                },
            },
        ]
    }
    return config
}

/**
 * Met à jour données + layout sans recréer le nœud DOM (`Plotly.animate`).
 * Ne pas enchaîner avec `xaxis.autorange` après l’animation : cela provoquait un
 * second recalcul de l’axe des abscisses et un saut visuel (ex. carte comparateur EM).
 * Le `layout` fourni par l’API contient déjà les plages / `autorange` nécessaires.
 * Repli : `Plotly.react` si l’animation échoue.
 *
 * ⚠️ L'animation ne sert PAS l'appelant qui pose lui-même son axe des temps
 * (`preserveLayoutRange`). Une transition de cinq cents millisecondes fait
 * passer l'axe par toutes les bornes intermédiaires entre l'ancienne fenêtre et
 * la nouvelle, et Plotly les annonce une à une : sur la page « Données du
 * compteur », c'est ce défilé qui se lisait comme un geste et défaisait le
 * recadrage demandé. Là, `Plotly.react` pose données et layout d'un seul coup.
 */
async function updatePlotDataInPlace(
    gd: Plotly.PlotlyHTMLElement,
    data: object[],
    layout: Record<string, unknown>,
    config: Record<string, unknown>
): Promise<void> {
    if (layoutPoseUnRange()) {
        await Plotly.react(gd, data, layout, config)
        return
    }
    const traces = data.map((_, i) => i)
    try {
        await Plotly.animate(
            gd,
            {
                data: data as Plotly.Data[],
                layout: layout as Partial<Plotly.Layout>,
                traces,
            },
            {
                transition: {
                    duration: DATA_UPDATE_ANIMATION_MS,
                    easing: DATA_UPDATE_EASING,
                },
                frame: {
                    duration: DATA_UPDATE_ANIMATION_MS,
                    redraw: true,
                },
            }
        )
    } catch {
        await Plotly.react(gd, data, layout, config)
    }
}

/**
 * Écoute `plotly_restyle` une seule fois (calcul min/max worker).
 */
function onPlotlyRestyle(data: unknown, gd: Plotly.PlotlyHTMLElement) {
    sendWorkerRestyleEvent(data, gd)
}

/**
 * Recalcule la taille du tracé pour la boîte DOM actuelle (`responsive` + conteneur en flex).
 */
function plotlyResizeToContainer(): void {
    const el = plotlyChart.value
    if (!el || !isPlotInitialized.value) return
    // Un redimensionnement recalcule les axes et fait émettre `plotly_relayout`
    // à Plotly : ce n'est pas un geste, il ne doit rien recadrer.
    void pendantUnRedessin(async () => {
        try {
            Plotly.Plots.resize(el)
        } catch {
            /* no-op */
        }
    })
}

/**
 * Écoute les changements de taille du nœud racine du graphique.
 */
function setupPlotResizeObserver(): void {
    const el = plotlyChart.value
    if (!el || typeof ResizeObserver === "undefined") return
    plotResizeObserver?.disconnect()
    plotResizeObserver = new ResizeObserver(() => {
        plotlyResizeToContainer()
    })
    plotResizeObserver.observe(el)
}

/**
 * Render le graphique Plotly en utilisant les données fournis dans graph
 * et ajoute un événement pour redimensionner le graphique lors du redimensionnement
 * de la fenêtre
 * @returns {void}
 */
const renderChart = async (): Promise<void> => {
    if (!props.graph) return

    const { data, layout } = props.graph
    const config = buildPlotConfig()

    if (!plotlyChart.value) return

    await pendantUnRedessin(() => dessiner(data, layout, config))

    await nextTick()
    plotlyResizeToContainer()
}

/**
 * Les redessins se suivent, ils ne se chevauchent pas.
 *
 * Deux mises à jour peuvent arriver coup sur coup — la fenêtre regardée change,
 * puis les points arrivent. Menées en parallèle, la plus ancienne peut finir la
 * dernière et laisser à l'écran l'axe d'AVANT. Cette file garde l'ordre : ce qui
 * est peint en dernier est ce qui a été demandé en dernier.
 */
let fileDesRedessins: Promise<void> = Promise.resolve()

function demanderUnRedessin(): Promise<void> {
    fileDesRedessins = fileDesRedessins.then(renderChart, renderChart)
    return fileDesRedessins
}

/** Le dessin lui-même. Séparé pour que le drapeau couvre TOUS ses chemins. */
async function dessiner(
    data: object[],
    layout: Record<string, unknown>,
    config: Record<string, unknown>
): Promise<void> {
    if (!plotlyChart.value) return
    if (!isPlotInitialized.value) {
        const plotlyLoaded = await Plotly.newPlot(
            plotlyChart.value,
            data,
            layout,
            config
        )
        plotlyGraphDiv.value = plotlyLoaded
        isPlotInitialized.value = true

        plotlyLoaded.on("plotly_restyle", (ev: unknown) => {
            onPlotlyRestyle(ev, plotlyLoaded)
        })

        // Cliquer-glisser sur l'axe des temps, et double-clic pour revenir.
        plotlyLoaded.on("plotly_relayout", (ev: unknown) => {
            if (redessinEnCours()) return
            emit("relayout", ev)
        })
        plotlyLoaded.on("plotly_doubleclick", () => {
            if (redessinEnCours()) return
            emit("doubleclick")
        })

        if (!layoutPoseUnRange()) {
            await Plotly.relayout(plotlyChart.value, {
                "xaxis.autorange": true,
            })
        }

        emit("loaded")
    } else {
        const gd = plotlyGraphDiv.value
        if (gd) {
            await updatePlotDataInPlace(gd, data, layout, config)
        }
    }
}

function sendWorkerRestyleEvent(
    data: any,
    plotlyLoaded: Plotly.PlotlyHTMLElement
) {
    const result = data as [{ visible: (string | boolean)[] }, number[]]
    const indexModified = result[1]
    const traces = plotlyLoaded.data.map((trace: any, index) => {
        // si dans le tableau des traces modifiées
        const r = {
            trace: trace.name,
            visible: trace.visible,
            showlegend: trace.showlegend,
        }

        if (indexModified.includes(index)) {
            const indexOf = result[1].indexOf(index)
            r.visible = result[0].visible[indexOf]
        }
        return r
    })
    if (!plotlyLoaded || !traces) return
    // récupère les données des traces visibles
    const visibleTraces = plotlyLoaded.data
        .filter((_, index) => {
            return (
                traces[index].visible &&
                traces[index].visible !== "legendonly" &&
                traces[index].showlegend
            )
        })

        .map((trace) => {
            // Forme le type de données attendu par le Web Worker
            const t = trace as PlotData
            return {
                type: t.type,
                data: t.y,
                yaxis: t.yaxis,
            }
        })
    // Envoyer les données au Web Worker (les traces visibles et la longueur des données)
    worker.postMessage({
        data: JSON.stringify(visibleTraces),
        barmode: (props.graph?.layout as any)?.barmode ?? "group",
    })
}

/**
 * Redimmensionne le graphique pour afficher l'axe des abscisses en entier
 */
function autoScale() {
    if (!plotlyChart.value) return
    const el = plotlyChart.value
    void pendantUnRedessin(async () => {
        await Plotly.relayout(el, { "xaxis.autorange": true })
    })
}

/**
 * Render le graphique lorsque le composant est monté
 */
onMounted(() => {
    void (async () => {
        await demanderUnRedessin()
        if (!layoutPoseUnRange()) autoScale()
        await nextTick()
        plotlyResizeToContainer()
        setupPlotResizeObserver()
    })()
})

onUnmounted(() => {
    plotResizeObserver?.disconnect()
    plotResizeObserver = null
    worker.terminate()
    if (plotlyChart.value && isPlotInitialized.value) {
        Plotly.purge(plotlyChart.value)
        isPlotInitialized.value = false
        plotlyGraphDiv.value = null
    }
})

/**
 * Observe les changements dans graph et redessine le graphique lorsque les données changent
 */
watch(
    () => props.graph,
    () => {
        void demanderUnRedessin()
    }
)

defineExpose({
    autoScale: autoScale,
})
</script>

<style scoped>
/* Taille réduite pour petits écrans */
@media (max-width: 768px) {
    #plotlyChart {
        height: 300px;
    }
}
</style>
